import { Button, Card, Segmented, Table, Typography } from 'antd'
import {
  SPEND_GROUPING_OPTIONS,
  SpendBreakdownRow,
  SpendGrouping,
  formatPct,
  formatPlanLabel,
  formatTokens,
  formatUsd,
} from './spendModel'

const { Text } = Typography

type Props = {
  rows: SpendBreakdownRow[]
  by: SpendGrouping
  /**
   * The grouping `rows` were fetched for. It lags `by` while a new grouping
   * loads, because the previous rows stay on screen until it does.
   */
  rowsBy?: SpendGrouping
  onChangeGrouping: (by: SpendGrouping) => void
  /** Filter the panel to one plan; offered on rows of the plan breakdown. */
  onSelectPlan: (plan: { value: string; label: string }) => void
  loading: boolean
}

const SpendBreakdownTable = ({
  rows,
  by,
  rowsBy,
  onChangeGrouping,
  onSelectPlan,
  loading,
}: Props) => {
  const columns = [
    {
      title: SPEND_GROUPING_OPTIONS.find((o) => o.value === by)?.label ?? 'Group',
      dataIndex: 'label',
      render: (label: string, record: SpendBreakdownRow) =>
        // A plan row filters the panel to that plan: then switch to Tenant
        // for tenant × plan.
        by === 'plan' ? (
          <Button type="link" size="small" style={{ padding: 0 }} onClick={() => onSelectPlan({ value: record.key, label: formatPlanLabel(record.key) })}>
            {formatPlanLabel(record.key)}
          </Button>
        ) : // Only tenants have a key worth copying; for the rest the key and
        // the label are the same value and a copy affordance would be noise.
        by === 'tenant' && record.key !== label ? (
          <>
            <div>{label}</div>
            <Text
              type="secondary"
              copyable={{ text: record.key, tooltips: ['Copy tenant GUID', 'Copied!'] }}
              style={{ fontSize: 12 }}
            >
              {record.key}
            </Text>
          </>
        ) : (
          label
        ),
    },
    // Tenant rows only: every other grouping's rows span tenants. Keyed on
    // the rows' own grouping so it never lands on another grouping's rows.
    ...(rowsBy === 'tenant'
      ? [
          {
            title: 'Tenant ID',
            dataIndex: 'tenant_id',
            render: (tenantId: number | null | undefined) =>
              tenantId == null ? (
                '—'
              ) : (
                <Text copyable={{ text: String(tenantId), tooltips: ['Copy tenant ID', 'Copied!'] }}>
                  {tenantId}
                </Text>
              ),
          },
        ]
      : []),
    {
      title: 'Spend',
      dataIndex: 'cost_usd',
      align: 'right' as const,
      defaultSortOrder: 'descend' as const,
      sorter: (a: SpendBreakdownRow, b: SpendBreakdownRow) =>
        parseFloat(a.cost_usd) - parseFloat(b.cost_usd),
      render: (value: string) => formatUsd(value),
    },
    {
      title: 'Saved',
      key: 'saved',
      align: 'right' as const,
      sorter: (a: SpendBreakdownRow, b: SpendBreakdownRow) =>
        a.savings_pct - b.savings_pct,
      render: (_: unknown, record: SpendBreakdownRow) => (
        <>
          {formatUsd(record.savings_usd)}{' '}
          <Text type="secondary">({formatPct(record.savings_pct)})</Text>
        </>
      ),
    },
    {
      title: 'Sessions',
      dataIndex: 'sessions',
      align: 'right' as const,
      sorter: (a: SpendBreakdownRow, b: SpendBreakdownRow) => a.sessions - b.sessions,
      render: (value: number) => formatTokens(value),
    },
    {
      title: 'Turns',
      dataIndex: 'turns',
      align: 'right' as const,
      sorter: (a: SpendBreakdownRow, b: SpendBreakdownRow) => a.turns - b.turns,
      render: (value: number) => formatTokens(value),
    },
  ]

  return (
    <Card
      size="small"
      title="Breakdown"
      extra={
        <Segmented
          value={by}
          options={SPEND_GROUPING_OPTIONS}
          onChange={(value) => onChangeGrouping(value as SpendGrouping)}
        />
      }
    >
      <Table
        rowKey="key"
        size="small"
        dataSource={rows}
        columns={columns}
        loading={loading}
        pagination={false}
      />
    </Card>
  )
}

export default SpendBreakdownTable
